import { useId, useMemo, useState } from "react";
import { Link } from "wouter";
import * as Sentry from "@sentry/react";
import { toast } from "@/hooks/use-toast";
import { useQuery as useConvexQuery, useMutation as useConvexMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";
import type { Doc, Id } from "../../../convex/_generated/dataModel";
import ScreenHeader from "@/components/mnf/ScreenHeader";
import Icon from "@/components/mnf/Icon";
import { GlassCard, PillTag, Btn } from "@/components/mnf/UI";
import SeasonChip from "@/components/mnf/SeasonChip";
import SeasonSetup from "@/components/mnf/SeasonSetup";
import VerseFlash from "@/components/mnf/VerseFlash";
import NutrientPanelCard from "@/components/mnf/NutrientPanelCard";
import {
  useStoreQuery,
  queries,
  mutations,
  tierAtLeast,
  autoMacros,
  ratiosToMacros,
  fixedKcal,
  tdee,
  mifflinStJeorWomen,
  seasonAdjustment,
  type ActivityLevel,
  type GoalDirection,
  type GoalPace,
  type MacroMode,
  type MacroPlan,
  type MacroProfile,
  type MealBlock,
  type Tier,
  type SeasonState,
  type MealVerse,
} from "@/lib/store";

/**
 * The food log lives in Convex `nutritionLogs` — the same itemized rows that
 * /garden appends to. This page shows her Breakfast / Lunch / Dinner / Snack
 * labels; only the stored value uses the Convex vocabulary.
 */
type NutritionMeal = Doc<"nutritionLogs">["meal"];
type NutritionLog = Doc<"nutritionLogs">;

const BLOCK_TO_MEAL: Record<MealBlock, NutritionMeal> = {
  breakfast: "morning",
  lunch: "midday",
  dinner: "evening",
  snack: "snack",
};

const MEAL_TO_BLOCK: Record<NutritionMeal, MealBlock> = {
  morning: "breakfast",
  midday: "lunch",
  evening: "dinner",
  snack: "snack",
};

type Totals = { kcal: number; proteinG: number; carbsG: number; fatG: number };

/**
 * Totals are always DERIVED by summing the itemized rows. Nothing stores a
 * per-day or per-block total, so a row appended by /garden and a row appended
 * here can never disagree about what the day adds up to.
 */
function sumEntries(rows: readonly NutritionLog[]): Totals {
  return rows.reduce<Totals>(
    (acc, r) => ({
      kcal: acc.kcal + r.kcal,
      proteinG: acc.proteinG + r.protein,
      carbsG: acc.carbsG + (r.carbs ?? 0),
      fatG: acc.fatG + (r.fat ?? 0),
    }),
    { kcal: 0, proteinG: 0, carbsG: 0, fatG: 0 },
  );
}

/** Local-time YYYY-MM-DD — the dateKey convention shared with Garden. */
function dateKeyFor(d: Date): string {
  return d.toLocaleDateString("en-CA");
}

/**
 * /macros - Macros & Targets, the Cronometer-inspired hub for MNF.
 * Three editor modes (Auto / Ratios / Fixed) gated by tier. Today's totals
 * are shown as forest rings, never red, never shame.
 */
export default function Macros() {
  const me = useStoreQuery(() => queries.currentUser());
  // No fallback to the seeded demo account. This screen both reads and writes a real
  // sister's food log, so the fallback rendered the seeded demo account's
  // targets and meals as if they were hers.
  const userId = me?.id ?? null;
  const tier: Tier = me?.tier ?? "Seedling";

  // Targets and season stay on the local store. Only the LOG moved.
  const profile = useStoreQuery(() => (userId ? queries.macroProfile(userId) : null));
  const plan = useStoreQuery(() => (userId ? queries.macroPlan(userId) : null));
  const season = useStoreQuery(() => (userId ? queries.seasonState(userId) : null));

  const todayKey = dateKeyFor(new Date());
  const weekKeys = useMemo(() => {
    const out: string[] = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      out.push(dateKeyFor(d));
    }
    return out;
    // todayKey rolls at midnight; recompute the rail with it.
  }, [todayKey]);

  const todayRows = useConvexQuery(api.nutrition.todayLogs, { dateKey: todayKey });
  const weekRows = useConvexQuery(api.nutrition.logsBetween, {
    fromDateKey: weekKeys[0],
    toDateKey: todayKey,
  });
  const logFood = useConvexMutation(api.nutrition.logFood);
  const deleteLog = useConvexMutation(api.nutrition.deleteLog);

  const todayLogs = useMemo(() => todayRows ?? [], [todayRows]);
  const todayTotals = useMemo(() => sumEntries(todayLogs), [todayLogs]);

  const entriesByBlock = useMemo(() => {
    const out: Record<MealBlock, NutritionLog[]> = {
      breakfast: [], lunch: [], dinner: [], snack: [],
    };
    for (const row of todayLogs) out[MEAL_TO_BLOCK[row.meal]].push(row);
    return out;
  }, [todayLogs]);

  const recentDays = useMemo(() => {
    const byDate = new Map<string, NutritionLog[]>();
    for (const row of weekRows ?? []) {
      const bucket = byDate.get(row.dateKey);
      if (bucket) bucket.push(row);
      else byDate.set(row.dateKey, [row]);
    }
    return weekKeys.map((date) => ({ date, ...sumEntries(byDate.get(date) ?? []) }));
  }, [weekRows, weekKeys]);

  const [editorOpen, setEditorOpen] = useState(false);
  const [seasonOpen, setSeasonOpen] = useState(false);
  const [flash, setFlash] = useState<{ verse: MealVerse; variant: "log" | "complete" } | null>(null);

  /**
   * Triggered after a sister appends an entry. Picks a contextual verse,
   * upgrades to the daily-complete blessing if she just crossed her kcal
   * goal for the first time today, and records the show so the verse pool
   * rotates.
   */
  const triggerVerse = (input: { block: MealBlock; kcal: number; proteinG: number }) => {
    if (!plan || !userId) return;
    // Entries append rather than replace, so the delta is simply the new entry
    // — there is no prior row for this block to subtract back out.
    const kcalBeforeThisLog = todayTotals.kcal;
    const newDailyKcal = kcalBeforeThisLog + input.kcal;
    const wasUnderGoal = kcalBeforeThisLog < plan.kcal * 0.9;
    const crossingGoal = wasUnderGoal && newDailyKcal >= plan.kcal * 0.9;
    const alreadyShown = queries.hasShownDailyCompleteToday(userId);

    if (crossingGoal && !alreadyShown) {
      const verse = queries.verseForDailyComplete(userId);
      if (verse) {
        mutations.recordVerseShown({ userId, verseId: verse.id, reason: "daily-complete" });
        setFlash({ verse, variant: "complete" });
        return;
      }
    }

    const isFirstMealOfDay = todayLogs.length === 0;
    const verse = queries.verseForMealContext({
      userId,
      block: input.block,
      proteinG: input.proteinG,
      kcal: input.kcal,
      isFirstMealOfDay,
    });
    if (verse) {
      mutations.recordVerseShown({
        userId,
        verseId: verse.id,
        reason: input.block === "breakfast" ? "morning"
          : input.block === "lunch" ? "midday"
          : input.block === "dinner" ? "evening"
          : "snack",
      });
      setFlash({ verse, variant: "log" });
    }
  };

  /**
   * Quick add APPENDS one entry to the block. It never touches the entries
   * already there — /garden writes real foods into these same meals and they
   * have to survive a quick add.
   */
  const handleQuickAdd = async (input: {
    block: MealBlock;
    kcal: number;
    proteinG: number;
    carbsG: number;
    fatG: number;
    note: string;
  }): Promise<boolean> => {
    try {
      await logFood({
        dateKey: todayKey,
        meal: BLOCK_TO_MEAL[input.block],
        // Her own words when she wrote some; otherwise an honest generic name
        // rather than inventing a food she did not log.
        name: input.note.trim() || "Quick add",
        serving: "1 serving",
        kcal: input.kcal,
        protein: input.proteinG,
        carbs: input.carbsG,
        fat: input.fatG,
      });
    } catch (err) {
      Sentry.captureException(err, { tags: { subsystem: "macros" } });
      toast({
        variant: "destructive",
        title: "That didn't save",
        description: "It's not in today's log yet. Everything you typed is still here — check your connection and try again.",
      });
      return false;
    }
    triggerVerse({ block: input.block, kcal: input.kcal, proteinG: input.proteinG });
    return true;
  };

  const handleDeleteEntry = async (id: Id<"nutritionLogs">): Promise<void> => {
    try {
      await deleteLog({ id });
    } catch (err) {
      Sentry.captureException(err, { tags: { subsystem: "macros" } });
      toast({
        variant: "destructive",
        title: "We couldn't remove that",
        description: "It's still in today's log. Please try again in a moment.",
      });
    }
  };

  const seasonAdj = seasonAdjustment(season);

  // Signed out, or the session is still resolving. Falling back to a seeded
  // account here would show one sister another sister's day, so show her
  // nothing of anyone's instead.
  if (!userId) {
    return (
      <>
        <Header tier={tier} season={null} />
        <div className="px-5 -mt-3">
          <GlassCard organic padding={28} style={{ textAlign: "center" }}>
            <span style={{ fontSize: 38, display: "block" }} aria-hidden>🌱</span>
            <p className="mnf-serif" style={{ fontSize: 20, fontWeight: 500, color: "var(--mnf-fd)", marginTop: 8 }}>
              Finding your account
            </p>
            <p className="mnf-body italic" style={{ fontSize: 13, color: "var(--mnf-tg)", lineHeight: 1.55, marginTop: 8, maxWidth: 300, marginLeft: "auto", marginRight: "auto" }}>
              Your targets and today's table will be here in a moment. If this stays, sign in again.
            </p>
          </GlassCard>
        </div>
      </>
    );
  }

  // Bootstrap: if no profile/plan exist, scaffold a sensible default for the
  // current sister so the page is never empty.
  if (!profile || !plan) {
    return (
      <>
        <Header tier={tier} season={season} onOpenSeason={() => setSeasonOpen(true)} />
        <div className="px-5 -mt-3">
          <GlassCard organic padding={28} style={{ textAlign: "center" }}>
            <span style={{ fontSize: 38, display: "block" }} aria-hidden>🌾</span>
            <p className="mnf-serif" style={{ fontSize: 20, fontWeight: 500, color: "var(--mnf-fd)", marginTop: 8 }}>
              Set up your macros
            </p>
            <p className="mnf-body italic" style={{ fontSize: 13, color: "var(--mnf-tg)", lineHeight: 1.55, marginTop: 8, maxWidth: 300, marginLeft: "auto", marginRight: "auto" }}>
              We'll calculate your daily kcal and protein, carbs, and fat from a few honest numbers. You can tune them anytime.
            </p>
            <div className="mt-4">
              <Btn
                variant="primary"
                onClick={() => {
                  mutations.saveMacroProfile({
                    userId,
                    seasonKind: "none",
                    ageYears: 32,
                    heightIn: 66,
                    weightLb: 145,
                    activity: "moderate",
                    direction: "maintain",
                    pace: "gentle",
                  });
                  const a = autoMacros({
                    userId,
                    seasonKind: "none",
                    ageYears: 32,
                    heightIn: 66,
                    weightLb: 145,
                    activity: "moderate",
                    direction: "maintain",
                    pace: "gentle",
                    updatedAt: Date.now(),
                  });
                  mutations.saveMacroPlan({
                    userId,
                    mode: "auto",
                    kcal: a.kcal,
                    proteinG: a.proteinG,
                    carbsG: a.carbsG,
                    fatG: a.fatG,
                    ratios: a.ratios,
                  });
                }}
              >
                Begin
              </Btn>
            </div>
          </GlassCard>
        </div>
      </>
    );
  }

  const dailyKcal = plan.kcal;
  const kcalPct = clamp01(todayTotals.kcal / Math.max(1, dailyKcal));
  const proteinPct = clamp01(todayTotals.proteinG / Math.max(1, plan.proteinG));
  const carbsPct = clamp01(todayTotals.carbsG / Math.max(1, plan.carbsG));
  const fatPct = clamp01(todayTotals.fatG / Math.max(1, plan.fatG));

  const remainingKcal = Math.max(0, plan.kcal - todayTotals.kcal);

  return (
    <>
      <Header tier={tier} season={season} onOpenSeason={() => setSeasonOpen(true)} />
      {seasonOpen && (
        <SeasonSetup userId={userId} current={season} onClose={() => setSeasonOpen(false)} />
      )}

      <div className="px-5 -mt-3 space-y-4 pb-6">
        {/* Provider export CTA */}
        <Link href="/provider-export">
          <a data-testid="button-open-provider-export" style={{ display: "block" }}>
            <GlassCard
              organic
              padding={16}
              style={{
                background: "linear-gradient(135deg, rgba(127,119,221,0.08) 0%, rgba(245,240,232,0.6) 100%)",
                border: "1px solid rgba(127,119,221,0.18)",
                cursor: "pointer",
              }}
            >
              <div className="flex items-start gap-3">
                <div
                  className="shrink-0 inline-flex items-center justify-center"
                  style={{ width: 36, height: 36, borderRadius: 12, background: "var(--mnf-purple, #7F77DD)", color: "#F5F0E8" }}
                >
                  <Icon name="print" size={18} weight={500} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)", marginBottom: 4 }}>
                    For your provider
                  </p>
                  <p className="mnf-serif" style={{ fontSize: 16, fontWeight: 500, color: "var(--mnf-fd)", marginBottom: 4 }}>
                    Share with my provider
                  </p>
                  <p className="mnf-body" style={{ fontSize: 13, color: "var(--mnf-tg)", lineHeight: 1.5 }}>
                    A clean summary you can hand your OB, midwife, or RD.
                  </p>
                </div>
                <Icon name="chevron_right" size={20} weight={400} color="var(--mnf-tg)" />
              </div>
            </GlassCard>
          </a>
        </Link>

        {/* Season banner */}
        {seasonAdj && (
          <GlassCard
            padding={16}
            style={{
              background: "linear-gradient(135deg, rgba(192,120,74,0.10) 0%, rgba(168,212,184,0.18) 100%)",
              border: "1px solid rgba(30,74,53,0.10)",
            }}
          >
            <div className="flex items-start gap-3">
              <div
                className="shrink-0 inline-flex items-center justify-center"
                style={{ width: 36, height: 36, borderRadius: 12, background: "var(--mnf-f)", color: "#F5F0E8" }}
              >
                <Icon name="child_friendly" size={18} weight={500} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)", marginBottom: 4 }}>
                  Season aware {seasonAdj.kcalDelta > 0 ? `· +${seasonAdj.kcalDelta} kcal` : "· GGS V2"}
                </p>
                <p className="mnf-serif" style={{ fontSize: 15, fontWeight: 500, color: "var(--mnf-fd)", marginBottom: 4 }}>
                  {seasonAdj.label}
                </p>
                <p className="mnf-body" style={{ fontSize: 12.5, color: "var(--mnf-tg)", lineHeight: 1.55 }}>
                  {seasonAdj.rationale}
                </p>
              </div>
            </div>
          </GlassCard>
        )}

        {/* Life-season micronutrient panel */}
        <NutrientPanelCard userId={userId} />

        {/* Coach lock banner */}
        {plan.locked && plan.source === "coach" && plan.coachNote && (
          <GlassCard
            padding={16}
            style={{
              background: "linear-gradient(135deg, rgba(30,74,53,0.97) 0%, rgba(22,56,40,0.97) 100%)",
              color: "#F5F0E8",
              border: "1px solid rgba(229,185,151,0.30)",
            }}
          >
            <div className="flex items-start gap-3">
              <div
                className="shrink-0 inline-flex items-center justify-center"
                style={{
                  width: 36, height: 36, borderRadius: 12,
                  background: "rgba(245,240,232,0.14)", color: "#F5F0E8",
                }}
              >
                <Icon name="lock" size={18} weight={500} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="mnf-eyebrow" style={{ color: "rgba(229,185,151,0.95)", marginBottom: 4 }}>
                  From Coach Kiley
                </p>
                <p className="mnf-body" style={{ fontSize: 13, lineHeight: 1.55, color: "#F5F0E8" }}>
                  {plan.coachNote}
                </p>
              </div>
            </div>
          </GlassCard>
        )}

        {/* Today ring */}
        <GlassCard padding={20}>
          <div className="flex items-end justify-between gap-3 mb-3">
            <div>
              <p className="mnf-eyebrow mb-1">Today's nourishment</p>
              <h2 className="mnf-serif" style={{ fontSize: 22, fontWeight: 500, color: "var(--mnf-fd)" }}>
                {Math.round(todayTotals.kcal)}
                <span className="mnf-body" style={{ fontSize: 13, color: "var(--mnf-tg)", fontWeight: 500 }}> of {dailyKcal} kcal</span>
              </h2>
            </div>
            <div className="text-right">
              <p className="mnf-eyebrow" style={{ color: "var(--mnf-fiber)" }}>Remaining</p>
              <p className="mnf-serif" style={{ fontSize: 18, fontWeight: 500, color: "var(--mnf-f)" }}>
                {remainingKcal} kcal
              </p>
            </div>
          </div>

          <div className="flex items-center justify-center my-2">
            <Ring
              pct={kcalPct}
              size={188}
              strokeWidth={14}
              centerLabel={
                <div style={{ textAlign: "center" }}>
                  <p className="mnf-serif" style={{ fontSize: 38, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1 }}>
                    {Math.round(kcalPct * 100)}%
                  </p>
                  <p className="mnf-eyebrow mt-1" style={{ color: "var(--mnf-tg)" }}>fueled</p>
                </div>
              }
            />
          </div>

          <div className="grid grid-cols-3 gap-2 mt-3">
            <MacroBar label="Protein" pct={proteinPct} have={todayTotals.proteinG} target={plan.proteinG} color="var(--mnf-fiber)" />
            <MacroBar label="Carbs"   pct={carbsPct}   have={todayTotals.carbsG}   target={plan.carbsG}   color="var(--mnf-purple)" />
            <MacroBar label="Fat"     pct={fatPct}     have={todayTotals.fatG}     target={plan.fatG}     color="var(--mnf-gold)" />
          </div>
        </GlassCard>

        {/* Targets summary + edit entry */}
        <GlassCard padding={18}>
          <div className="flex items-start justify-between gap-3 mb-2">
            <div>
              <p className="mnf-eyebrow mb-1">Your daily targets</p>
              <h3 className="mnf-serif" style={{ fontSize: 18, fontWeight: 500, color: "var(--mnf-fd)" }}>
                Macros & energy
              </h3>
            </div>
            <PillTag tone={plan.mode === "auto" ? "fiber" : plan.mode === "ratios" ? "purple" : "gold"}>
              {plan.mode === "auto" ? "Auto" : plan.mode === "ratios" ? "Ratios" : "Fixed"}
            </PillTag>
          </div>

          <div className="grid grid-cols-4 gap-2 mt-3">
            <TargetTile label="kcal"    value={plan.kcal}     accent="var(--mnf-f)" />
            <TargetTile label="Protein" value={plan.proteinG} suffix="g" accent="var(--mnf-fiber)" />
            <TargetTile label="Carbs"   value={plan.carbsG}   suffix="g" accent="var(--mnf-purple)" />
            <TargetTile label="Fat"     value={plan.fatG}     suffix="g" accent="var(--mnf-gold)" />
          </div>

          {plan.mode === "auto" && (
            <p className="mnf-body italic" style={{ fontSize: 12, color: "var(--mnf-tg)", marginTop: 10, lineHeight: 1.5 }}>
              Calculated from your stats. BMR {Math.round(mifflinStJeorWomen(profile.weightLb, profile.heightIn, profile.ageYears))} · TDEE {tdee(profile)}.
            </p>
          )}

          <div className="mt-3 flex items-center gap-2">
            <Btn
              variant="secondary"
              size="sm"
              onClick={() => setEditorOpen((v) => !v)}
              disabled={plan.locked && plan.source === "coach"}
            >
              {editorOpen ? "Close editor" : (plan.locked ? "Locked by coach" : "Adjust targets")}
            </Btn>
            <span className="mnf-body" style={{ fontSize: 11.5, color: "var(--mnf-tg)" }}>
              {modesAvailable(tier).length} mode{modesAvailable(tier).length === 1 ? "" : "s"} on {tier}
            </span>
          </div>
        </GlassCard>

        {editorOpen && !plan.locked && (
          <Editor
            tier={tier}
            profile={profile}
            plan={plan}
            onClose={() => setEditorOpen(false)}
          />
        )}

        {/* Meal blocks */}
        <SectionTitle eyebrow="Today's table" title={<>Meals & <span className="italic">moments</span></>} />
        <div className="space-y-3">
          {(["breakfast", "lunch", "dinner", "snack"] as const).map((block) => (
            <MealBlockCard
              key={block}
              block={block}
              entries={entriesByBlock[block]}
              onQuickAdd={handleQuickAdd}
              onDelete={handleDeleteEntry}
            />
          ))}
        </div>

        {/* 7-day trend */}
        <SectionTitle eyebrow="The last seven days" title={<>Walking <span className="italic">faithfully</span></>} />
        <GlassCard padding={16}>
          <div className="flex items-end justify-between gap-1.5" style={{ height: 88 }}>
            {recentDays.map((d, i) => {
              const pct = clamp01(d.kcal / Math.max(1, plan.kcal));
              const isToday = i === recentDays.length - 1;
              const dateLabel = new Date(d.date + "T12:00:00").toLocaleDateString("en-US", { weekday: "narrow" });
              return (
                <div key={d.date} className="flex-1 flex flex-col items-center justify-end gap-1.5" style={{ height: "100%" }}>
                  <div
                    style={{
                      width: "100%",
                      height: `${Math.max(6, pct * 76)}px`,
                      borderRadius: 8,
                      background: isToday
                        ? "linear-gradient(180deg, var(--mnf-f) 0%, #2C6248 100%)"
                        : "rgba(30,74,53,0.18)",
                      transition: "height 0.4s ease",
                    }}
                  />
                  <span className="mnf-eyebrow" style={{ fontSize: 9, color: "var(--mnf-tg)" }}>{dateLabel}</span>
                </div>
              );
            })}
          </div>
        </GlassCard>

        {/* Scripture footer (season aware) */}
        <GlassCard organic padding={20} style={{ textAlign: "center" }}>
          <p className="mnf-serif italic" style={{ fontSize: 15, color: "var(--mnf-fd)", lineHeight: 1.55 }}>
            "{seasonAdj?.scripture.text ?? "He causes the grass to grow for the cattle, and plants for people to cultivate, bringing forth food from the earth."}"
          </p>
          <p className="mnf-eyebrow" style={{ color: "var(--mnf-purple)", marginTop: 8 }}>{seasonAdj?.scripture.ref ?? "Psalm 104:14"}</p>
        </GlassCard>
      </div>

      {flash && (
        <VerseFlash
          verse={flash.verse}
          variant={flash.variant}
          durationMs={flash.variant === "complete" ? 4200 : 3000}
          onDone={() => setFlash(null)}
        />
      )}
    </>
  );
}

function Header({
  tier, season, onOpenSeason,
}: {
  tier: Tier;
  season: SeasonState | null;
  /** Omitted when there is no signed-in sister to set a season for. */
  onOpenSeason?: () => void;
}) {
  return (
    <ScreenHeader
      variant="forest"
      eyebrow="Macros & targets"
      title={<>Nourish with <span className="italic">intention</span></>}
      subtitle={
        <>
          Your daily kcal and macros, calculated honestly. Tune them as your season shifts.
          <span style={{ color: "rgba(229,185,151,0.95)" }}> · {tier}</span>
          {onOpenSeason && season && season.kind !== "none" && (
            <span className="block mt-2">
              <SeasonChip season={season} variant="onForest" onClick={onOpenSeason} testId="chip-season-header" />
            </span>
          )}
          {onOpenSeason && (!season || season.kind === "none") && (
            <button
              onClick={onOpenSeason}
              className="hover-elevate mt-2"
              data-testid="button-set-season"
              style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                padding: "5px 11px", borderRadius: 999,
                background: "rgba(245,240,232,0.16)", color: "#F5F0E8",
                fontSize: 11, fontWeight: 700, letterSpacing: "0.06em",
                textTransform: "uppercase", border: "none", cursor: "pointer", fontFamily: "inherit",
              }}
            >
              <Icon name="add" size={11} /> Set my season
            </button>
          )}
        </>
      }
      trailing={
        <Link href="/nourish">
          <span
            className="inline-flex items-center justify-center"
            style={{
              width: 36, height: 36, borderRadius: 999,
              background: "rgba(245,240,232,0.14)", color: "#F5F0E8", cursor: "pointer",
            }}
          >
            <Icon name="arrow_back" size={18} weight={500} />
          </span>
        </Link>
      }
    />
  );
}

/* -------------------------------- Helpers -------------------------------- */

function clamp01(n: number): number {
  if (!Number.isFinite(n) || n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

function modesAvailable(tier: Tier): MacroMode[] {
  if (tierAtLeast(tier, "Flourishing")) return ["auto", "ratios", "fixed"];
  if (tierAtLeast(tier, "Rooted")) return ["auto", "ratios"];
  return ["auto"];
}

/* ---------------------------- Section title ------------------------------ */

function SectionTitle({ eyebrow, title }: { eyebrow: string; title: React.ReactNode }) {
  return (
    <div>
      <p className="mnf-eyebrow mb-1">{eyebrow}</p>
      <h2 className="mnf-serif" style={{ fontSize: 22, fontWeight: 500, color: "var(--mnf-fd)" }}>{title}</h2>
    </div>
  );
}

/* ---------------------------- SVG progress ring -------------------------- */

function Ring({
  pct,
  size = 160,
  strokeWidth = 12,
  centerLabel,
}: {
  pct: number;
  size?: number;
  strokeWidth?: number;
  centerLabel?: React.ReactNode;
}) {
  const r = (size - strokeWidth) / 2;
  const circ = 2 * Math.PI * r;
  const dash = circ * pct;
  return (
    <div style={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle
          cx={size / 2} cy={size / 2} r={r}
          stroke="rgba(30,74,53,0.10)" strokeWidth={strokeWidth} fill="none"
        />
        <circle
          cx={size / 2} cy={size / 2} r={r}
          stroke="var(--mnf-f)" strokeWidth={strokeWidth} fill="none"
          strokeDasharray={`${dash} ${circ - dash}`}
          strokeLinecap="round"
          style={{ transition: "stroke-dasharray 0.6s ease" }}
        />
      </svg>
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{ pointerEvents: "none" }}
      >
        {centerLabel}
      </div>
    </div>
  );
}

/* ---------------------------- Macro mini bar ----------------------------- */

function MacroBar({
  label, pct, have, target, color,
}: {
  label: string; pct: number; have: number; target: number; color: string;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between" style={{ marginBottom: 4 }}>
        <span className="mnf-eyebrow" style={{ color: "var(--mnf-tg)", fontSize: 9.5 }}>{label}</span>
        <span className="mnf-body" style={{ fontSize: 11, fontWeight: 700, color: "var(--mnf-fd)" }}>
          {Math.round(have)}<span style={{ color: "var(--mnf-tg)", fontWeight: 500 }}>/{target}g</span>
        </span>
      </div>
      <div style={{ height: 8, borderRadius: 999, background: "rgba(30,74,53,0.08)", overflow: "hidden" }}>
        <div
          style={{
            width: `${Math.max(2, pct * 100)}%`,
            height: "100%",
            background: color,
            transition: "width 0.5s ease",
            borderRadius: 999,
          }}
        />
      </div>
    </div>
  );
}

/* ---------------------------- Target tile -------------------------------- */

function TargetTile({
  label, value, suffix, accent,
}: {
  label: string; value: number; suffix?: string; accent: string;
}) {
  return (
    <div
      style={{
        background: "rgba(30,74,53,0.04)",
        borderRadius: 16,
        padding: "10px 8px",
        textAlign: "center",
        border: `1px solid ${accent.replace(")", ", 0.18)").replace("var(", "color-mix(in srgb, var(")}`,
      }}
    >
      <p className="mnf-eyebrow" style={{ color: accent, fontSize: 9.5 }}>{label}</p>
      <p className="mnf-serif" style={{ fontSize: 20, fontWeight: 500, color: "var(--mnf-fd)", marginTop: 2 }}>
        {value}{suffix && <span className="mnf-body" style={{ fontSize: 11, color: "var(--mnf-tg)", fontWeight: 500 }}>{suffix}</span>}
      </p>
    </div>
  );
}

/* ============================== Editor =================================== */

function Editor({
  tier, profile, plan, onClose,
}: {
  tier: Tier;
  profile: MacroProfile;
  plan: MacroPlan;
  onClose: () => void;
}) {
  const modes = modesAvailable(tier);
  const [tab, setTab] = useState<MacroMode>(plan.mode);

  return (
    <GlassCard padding={18}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="mnf-eyebrow mb-1">Adjust your targets</p>
          <h3 className="mnf-serif" style={{ fontSize: 18, fontWeight: 500, color: "var(--mnf-fd)" }}>
            Set macros using
          </h3>
        </div>
        <button
          onClick={onClose}
          aria-label="Close editor"
          className="inline-flex items-center justify-center"
          style={{
            width: 32, height: 32, borderRadius: 999,
            background: "rgba(30,74,53,0.06)", color: "var(--mnf-f)",
            border: "none", cursor: "pointer",
          }}
        >
          <Icon name="close" size={16} weight={500} />
        </button>
      </div>

      {/* Mode tabs */}
      <div
        className="flex items-center gap-1 p-1 mb-4"
        style={{ background: "rgba(30,74,53,0.06)", borderRadius: 999 }}
      >
        {(["auto", "ratios", "fixed"] as const).map((m) => {
          const enabled = modes.includes(m);
          const active = tab === m;
          return (
            <button
              key={m}
              onClick={() => enabled && setTab(m)}
              disabled={!enabled}
              className="flex-1 mnf-body transition-all"
              style={{
                padding: "8px 10px",
                borderRadius: 999,
                background: active ? "var(--mnf-fd)" : "transparent",
                color: active ? "#F5F0E8" : enabled ? "var(--mnf-f)" : "rgba(30,74,53,0.35)",
                fontSize: 12,
                fontWeight: 600,
                border: "none",
                cursor: enabled ? "pointer" : "not-allowed",
                textTransform: "capitalize",
                letterSpacing: "0.04em",
                display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 4,
              }}
            >
              {m}
              {!enabled && <Icon name="lock" size={11} weight={500} />}
            </button>
          );
        })}
      </div>

      {tab === "auto" && <AutoEditor profile={profile} plan={plan} />}
      {tab === "ratios" && <RatiosEditor profile={profile} plan={plan} />}
      {tab === "fixed" && <FixedEditor plan={plan} />}
    </GlassCard>
  );
}

/* -------------------------- Auto mode editor ----------------------------- */

function AutoEditor({ profile, plan }: { profile: MacroProfile; plan: MacroPlan }) {
  const [age, setAge] = useState(profile.ageYears);
  const [heightIn, setHeightIn] = useState(profile.heightIn);
  const [weightLb, setWeightLb] = useState(profile.weightLb);
  const [activity, setActivity] = useState<ActivityLevel>(profile.activity);
  const [direction, setDirection] = useState<GoalDirection>(profile.direction);
  const [pace, setPace] = useState<GoalPace>(profile.pace);

  const previewProfile: MacroProfile = useMemo(
    () => ({ ...profile, ageYears: age, heightIn, weightLb, activity, direction, pace }),
    [profile, age, heightIn, weightLb, activity, direction, pace],
  );
  const preview = useMemo(() => autoMacros(previewProfile), [previewProfile]);

  const save = () => {
    mutations.saveMacroProfile({
      userId: profile.userId,
      seasonKind: profile.seasonKind,
      ageYears: age,
      heightIn,
      weightLb,
      activity,
      direction,
      pace,
    });
    mutations.saveMacroPlan({
      userId: plan.userId,
      mode: "auto",
      kcal: preview.kcal,
      proteinG: preview.proteinG,
      carbsG: preview.carbsG,
      fatG: preview.fatG,
      ratios: preview.ratios,
    });
  };

  return (
    <div className="space-y-3">
      <p className="mnf-body" style={{ fontSize: 12.5, color: "var(--mnf-tg)", lineHeight: 1.55 }}>
        We use Mifflin-St Jeor to estimate your base, then adjust for your activity and the season you're in. Honest numbers in, honest plan out.
      </p>

      <div className="grid grid-cols-3 gap-2">
        <NumField label="Age" value={age} onChange={setAge} suffix="yr" min={16} max={90} />
        <NumField label="Height" value={heightIn} onChange={setHeightIn} suffix="in" min={48} max={84} />
        <NumField label="Weight" value={weightLb} onChange={setWeightLb} suffix="lb" min={80} max={400} />
      </div>

      <SegRow
        label="Activity"
        value={activity}
        onChange={(v) => setActivity(v as ActivityLevel)}
        options={[
          { value: "sedentary", label: "Sedentary" },
          { value: "light", label: "Light" },
          { value: "moderate", label: "Moderate" },
          { value: "active", label: "Active" },
          { value: "very_active", label: "Very" },
        ]}
      />

      <SegRow
        label="Goal"
        value={direction}
        onChange={(v) => setDirection(v as GoalDirection)}
        options={[
          { value: "release", label: "Release" },
          { value: "maintain", label: "Maintain" },
          { value: "build", label: "Build" },
        ]}
      />

      {direction !== "maintain" && (
        <SegRow
          label="Pace"
          value={pace}
          onChange={(v) => setPace(v as GoalPace)}
          options={[
            { value: "gentle", label: "Gentle" },
            { value: "steady", label: "Steady" },
            { value: "strong", label: "Strong" },
          ]}
        />
      )}

      {/* Live preview */}
      <div
        style={{
          background: "linear-gradient(135deg, rgba(168,212,184,0.18) 0%, rgba(192,120,74,0.10) 100%)",
          borderRadius: 16,
          padding: 14,
        }}
      >
        <p className="mnf-eyebrow mb-2" style={{ color: "var(--mnf-f)" }}>Live preview</p>
        <div className="grid grid-cols-4 gap-2">
          <TargetTile label="kcal"    value={preview.kcal}     accent="var(--mnf-f)" />
          <TargetTile label="Protein" value={preview.proteinG} suffix="g" accent="var(--mnf-fiber)" />
          <TargetTile label="Carbs"   value={preview.carbsG}   suffix="g" accent="var(--mnf-purple)" />
          <TargetTile label="Fat"     value={preview.fatG}     suffix="g" accent="var(--mnf-gold)" />
        </div>
        <p className="mnf-body italic" style={{ fontSize: 11.5, color: "var(--mnf-tg)", marginTop: 8, lineHeight: 1.5 }}>
          BMR {Math.round(mifflinStJeorWomen(weightLb, heightIn, age))} · TDEE {tdee(previewProfile)}
        </p>
      </div>

      <Btn variant="primary" block onClick={save}>Save targets</Btn>
    </div>
  );
}

/* -------------------------- Ratios mode editor --------------------------- */

function RatiosEditor({ profile, plan }: { profile: MacroProfile; plan: MacroPlan }) {
  const [kcal, setKcal] = useState(plan.kcal);
  const [p, setP] = useState(plan.ratios.p);
  const [c, setC] = useState(plan.ratios.c);
  const f = Math.max(0, 100 - p - c);

  const macros = useMemo(() => ratiosToMacros(kcal, { p, c, f }), [kcal, p, c, f]);
  const sumOk = p + c + f === 100;

  const save = () => {
    if (!sumOk) return;
    mutations.saveMacroPlan({
      userId: plan.userId,
      mode: "ratios",
      kcal,
      proteinG: macros.proteinG,
      carbsG: macros.carbsG,
      fatG: macros.fatG,
      ratios: { p, c, f },
    });
  };

  return (
    <div className="space-y-3">
      <p className="mnf-body" style={{ fontSize: 12.5, color: "var(--mnf-tg)", lineHeight: 1.55 }}>
        Set how your kcal divide between protein, carbs, and fat. The three percentages always sum to one hundred.
      </p>

      <NumField label="Energy target" value={kcal} onChange={setKcal} suffix="kcal" min={1000} max={4500} step={10} />

      <RatioRow color="var(--mnf-fiber)" label="Protein" pct={p} onChange={(v) => setP(clampPct(v, 100 - c))} grams={macros.proteinG} kcal={macros.proteinG * 4} />
      <RatioRow color="var(--mnf-purple)" label="Net carbs" pct={c} onChange={(v) => setC(clampPct(v, 100 - p))} grams={macros.carbsG} kcal={macros.carbsG * 4} />
      <RatioRow color="var(--mnf-gold)" label="Fat" pct={f} onChange={() => { /* derived */ }} grams={macros.fatG} kcal={macros.fatG * 9} disabled />

      <div className="flex items-center justify-between" style={{ paddingTop: 4 }}>
        <p className="mnf-body" style={{ fontSize: 12, color: "var(--mnf-tg)" }}>
          Calculated from your weight goal: <strong style={{ color: "var(--mnf-f)" }}>{tdee(profile)} kcal</strong>
        </p>
      </div>

      <Btn variant="primary" block onClick={save} disabled={!sumOk}>Save targets</Btn>
    </div>
  );
}

function clampPct(v: number, max: number): number {
  if (!Number.isFinite(v) || v < 0) return 0;
  if (v > max) return max;
  return Math.round(v);
}

function RatioRow({
  color, label, pct, onChange, grams, kcal, disabled,
}: {
  color: string; label: string; pct: number; onChange: (v: number) => void;
  grams: number; kcal: number; disabled?: boolean;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <span style={{ width: 10, height: 10, borderRadius: 999, background: color, display: "inline-block" }} aria-hidden />
          <span className="mnf-body" style={{ fontSize: 13, fontWeight: 700, color: "var(--mnf-fd)" }}>{label}</span>
          <span className="mnf-body" style={{ fontSize: 12, color: "var(--mnf-tg)" }}>{pct}%</span>
        </div>
        <span className="mnf-body" style={{ fontSize: 12, color: "var(--mnf-tg)" }}>
          {Math.round(kcal)} kcal · {grams}g
        </span>
      </div>
      <input
        type="range" min={0} max={100} step={1} value={pct}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full"
        style={{ accentColor: color }}
      />
    </div>
  );
}

/* --------------------------- Fixed mode editor --------------------------- */

function FixedEditor({ plan }: { plan: MacroPlan }) {
  const [p, setP] = useState(plan.proteinG);
  const [c, setC] = useState(plan.carbsG);
  const [f, setF] = useState(plan.fatG);
  const total = fixedKcal(p, c, f);
  const ratios = useMemo(() => {
    if (total === 0) return { p: 0, c: 0, f: 0 };
    const pp = Math.round(((p * 4) / total) * 100);
    const cc = Math.round(((c * 4) / total) * 100);
    return { p: pp, c: cc, f: 100 - pp - cc };
  }, [p, c, f, total]);

  const save = () => {
    mutations.saveMacroPlan({
      userId: plan.userId,
      mode: "fixed",
      kcal: total,
      proteinG: p,
      carbsG: c,
      fatG: f,
      ratios,
    });
  };

  return (
    <div className="space-y-3">
      <p className="mnf-body" style={{ fontSize: 12.5, color: "var(--mnf-tg)", lineHeight: 1.55 }}>
        Type the daily grams you want for each macro. We'll show the kcal that those add up to.
      </p>

      <div className="grid grid-cols-3 gap-2">
        <NumField label="Protein" value={p} onChange={setP} suffix="g" min={0} max={400} accent="var(--mnf-fiber)" />
        <NumField label="Carbs"   value={c} onChange={setC} suffix="g" min={0} max={600} accent="var(--mnf-purple)" />
        <NumField label="Fat"     value={f} onChange={setF} suffix="g" min={0} max={300} accent="var(--mnf-gold)" />
      </div>

      <div
        style={{
          background: "rgba(30,74,53,0.04)",
          borderRadius: 16,
          padding: 14,
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
        }}
      >
        <div>
          <p className="mnf-eyebrow" style={{ color: "var(--mnf-f)" }}>Energy total</p>
          <p className="mnf-serif" style={{ fontSize: 24, fontWeight: 500, color: "var(--mnf-fd)" }}>{total} kcal</p>
        </div>
        <div className="text-right">
          <p className="mnf-eyebrow" style={{ color: "var(--mnf-tg)" }}>Split</p>
          <p className="mnf-body" style={{ fontSize: 12, color: "var(--mnf-fd)" }}>
            {ratios.p}% · {ratios.c}% · {ratios.f}%
          </p>
        </div>
      </div>

      <Btn variant="primary" block onClick={save}>Save targets</Btn>
    </div>
  );
}

/* ----------------------------- Form atoms -------------------------------- */

function NumField({
  label, value, onChange, suffix, min, max, step, accent,
}: {
  label: string; value: number; onChange: (n: number) => void;
  suffix?: string; min?: number; max?: number; step?: number; accent?: string;
}) {
  // Explicit htmlFor/id — the visible label text (KCAL / PROTEIN g / etc.)
  // sits in its own <span>, so relying on implicit label-wraps-input
  // association is fragile. This makes the pairing unambiguous for every
  // screen reader.
  const inputId = useId();
  return (
    <label
      htmlFor={inputId}
      className="flex flex-col"
      style={{
        background: "#fff",
        border: `1px solid ${accent ? "rgba(30,74,53,0.18)" : "rgba(30,74,53,0.12)"}`,
        borderRadius: 14, padding: "8px 12px",
      }}
    >
      <span className="mnf-eyebrow" style={{ color: accent ?? "var(--mnf-tg)", fontSize: 9.5 }}>{label}</span>
      <span className="flex items-baseline gap-1">
        <input
          id={inputId}
          type="number" inputMode="numeric"
          min={min} max={max} step={step ?? 1}
          placeholder="0"
          aria-label={suffix ? `${label} (${suffix})` : label}
          value={Number.isFinite(value) ? value : 0}
          onChange={(e) => onChange(Number(e.target.value))}
          className="mnf-serif"
          style={{
            width: "100%", border: "none", outline: "none", background: "transparent",
            fontSize: 19, fontWeight: 500, color: "var(--mnf-fd)", padding: 0,
          }}
        />
        {suffix && <span className="mnf-body" style={{ fontSize: 11, color: "var(--mnf-tg)" }}>{suffix}</span>}
      </span>
    </label>
  );
}

function SegRow({
  label, value, onChange, options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <div>
      <p className="mnf-eyebrow mb-1.5">{label}</p>
      <div className="flex items-center gap-1 flex-wrap">
        {options.map((o) => {
          const active = o.value === value;
          return (
            <button
              key={o.value}
              onClick={() => onChange(o.value)}
              className="mnf-body transition-all"
              style={{
                padding: "7px 12px",
                borderRadius: 999,
                background: active ? "var(--mnf-f)" : "rgba(30,74,53,0.06)",
                color: active ? "#F5F0E8" : "var(--mnf-f)",
                border: "none",
                fontSize: 11.5, fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------------------- Meal block card ---------------------------- */

const BLOCK_META: Record<MealBlock, { label: string; icon: string; bg: string }> = {
  breakfast: { label: "Breakfast", icon: "wb_sunny",  bg: "rgba(160,120,32,0.08)" },
  lunch:     { label: "Lunch",     icon: "restaurant", bg: "rgba(168,212,184,0.18)" },
  dinner:    { label: "Dinner",    icon: "nights_stay", bg: "rgba(127,119,221,0.10)" },
  snack:     { label: "Snack",     icon: "cookie",    bg: "rgba(192,120,74,0.10)" },
};

/**
 * One meal block: its itemized entries, a subtotal derived from them, and a
 * quick add that APPENDS a single entry. Entries logged from /garden land in
 * these same blocks, so nothing here may overwrite or clear a sibling entry —
 * removal is always per-entry and explicit.
 */
function MealBlockCard({
  block, entries, onQuickAdd, onDelete,
}: {
  block: MealBlock;
  entries: readonly NutritionLog[];
  onQuickAdd: (input: {
    block: MealBlock;
    kcal: number;
    proteinG: number;
    carbsG: number;
    fatG: number;
    note: string;
  }) => Promise<boolean>;
  onDelete: (id: Id<"nutritionLogs">) => Promise<void>;
}) {
  const meta = BLOCK_META[block];
  const [open, setOpen] = useState(false);
  const panelId = `meal-block-panel-${block}`;
  const noteInputId = useId();
  const [kcal, setKcal] = useState(0);
  const [p, setP] = useState(0);
  const [c, setC] = useState(0);
  const [f, setF] = useState(0);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const subtotal = sumEntries(entries);

  const save = async (): Promise<void> => {
    if (kcal <= 0 && p <= 0 && c <= 0 && f <= 0) {
      // Empty save, no-op so we don't append a blank row or show a verse for nothing.
      setOpen(false);
      return;
    }
    setSaving(true);
    const ok = await onQuickAdd({ block, kcal, proteinG: p, carbsG: c, fatG: f, note });
    setSaving(false);
    // On failure, leave the sheet open with everything she typed still in it.
    if (!ok) return;
    // Quick add appends, so clear the form — a second tap should add a second
    // entry, not re-add the one that just landed.
    setKcal(0);
    setP(0);
    setC(0);
    setF(0);
    setNote("");
    setOpen(false);
  };

  return (
    <GlassCard padding={0} style={{ overflow: "hidden" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left transition-all"
        data-testid={`button-meal-block-${block}`}
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={`Log ${meta.label}`}
        style={{
          padding: 14, background: meta.bg, border: "none", cursor: "pointer",
          display: "flex", alignItems: "center", gap: 12,
        }}
      >
        <div
          className="shrink-0 inline-flex items-center justify-center"
          style={{
            width: 38, height: 38, borderRadius: 12, background: "#fff", color: "var(--mnf-f)",
          }}
        >
          <Icon name={meta.icon} size={18} weight={500} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="mnf-body" style={{ fontSize: 13, fontWeight: 700, color: "var(--mnf-fd)" }}>{meta.label}</p>
          {entries.length > 0 ? (
            <p className="mnf-body" style={{ fontSize: 12, color: "var(--mnf-tg)" }}>
              {entries.length} item{entries.length === 1 ? "" : "s"} · {Math.round(subtotal.kcal)} kcal
            </p>
          ) : (
            <p className="mnf-body italic" style={{ fontSize: 12, color: "var(--mnf-tg)" }}>
              Tap to log this meal
            </p>
          )}
        </div>
        <Icon name={open ? "expand_less" : "expand_more"} size={18} weight={500} />
      </button>

      {entries.length > 0 && (
        <ul style={{ borderTop: "1px solid rgba(30,74,53,0.08)" }}>
          {entries.map((row) => (
            <li
              key={row._id}
              className="flex items-center gap-3"
              style={{ padding: "10px 14px", borderBottom: "1px solid rgba(30,74,53,0.06)" }}
              data-testid={`entry-${block}-${row._id}`}
            >
              <div className="flex-1 min-w-0">
                <p className="mnf-body truncate" style={{ fontSize: 13, fontWeight: 600, color: "var(--mnf-fd)" }}>
                  {row.name}
                </p>
                <p className="mnf-body" style={{ fontSize: 11, color: "var(--mnf-tg)" }}>
                  {row.serving} · {Math.round(row.kcal)} kcal · {Math.round(row.protein)}p
                  {row.carbs !== undefined ? ` · ${Math.round(row.carbs)}c` : ""}
                  {row.fat !== undefined ? ` · ${Math.round(row.fat)}f` : ""}
                </p>
              </div>
              <button
                onClick={() => { void onDelete(row._id); }}
                aria-label={`Remove ${row.name}`}
                data-testid={`button-remove-entry-${row._id}`}
                className="shrink-0 inline-flex items-center justify-center hover-elevate"
                style={{
                  width: 30, height: 30, borderRadius: 999,
                  background: "rgba(192,120,74,0.10)", color: "#C0784A",
                  border: "none", cursor: "pointer",
                }}
              >
                <Icon name="delete" size={15} weight={500} />
              </button>
            </li>
          ))}
          <li
            className="flex items-center justify-between"
            style={{ padding: "8px 14px", background: "rgba(30,74,53,0.03)" }}
            data-testid={`subtotal-${block}`}
          >
            <span className="mnf-eyebrow" style={{ color: "var(--mnf-tg)", fontSize: 9.5 }}>Subtotal</span>
            <span className="mnf-body" style={{ fontSize: 12, fontWeight: 700, color: "var(--mnf-fd)" }}>
              {Math.round(subtotal.kcal)} kcal · {Math.round(subtotal.proteinG)}p · {Math.round(subtotal.carbsG)}c · {Math.round(subtotal.fatG)}f
            </span>
          </li>
        </ul>
      )}

      {open && (
        <div id={panelId} className="p-4" style={{ borderTop: "1px solid rgba(30,74,53,0.08)" }}>
          <p className="mnf-eyebrow mb-2" style={{ color: "var(--mnf-tg)" }}>Quick add</p>
          <div className="grid grid-cols-4 gap-2 mb-3">
            <NumField label="kcal"    value={kcal} onChange={setKcal} min={0} max={3000} step={10} />
            <NumField label="Protein" value={p}    onChange={setP}    suffix="g" min={0} max={250} />
            <NumField label="Carbs"   value={c}    onChange={setC}    suffix="g" min={0} max={400} />
            <NumField label="Fat"     value={f}    onChange={setF}    suffix="g" min={0} max={200} />
          </div>
          <label htmlFor={noteInputId} className="flex flex-col mb-3">
            <span className="mnf-eyebrow mb-1" style={{ color: "var(--mnf-tg)" }}>What was this?</span>
            <input
              id={noteInputId}
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Oatmeal and berries — or leave it blank"
              aria-label="What was this?"
              className="mnf-body"
              data-testid={`input-quick-add-name-${block}`}
              style={{
                background: "#fff",
                border: "1px solid rgba(30,74,53,0.12)",
                borderRadius: 12,
                padding: "10px 12px",
                fontSize: 13, color: "var(--mnf-fd)",
                outline: "none",
              }}
            />
          </label>
          <div className="flex items-center gap-2">
            <Btn
              variant="primary"
              size="sm"
              disabled={saving}
              onClick={() => { void save(); }}
              data-testid={`button-quick-add-${block}`}
            >
              {saving ? "Adding…" : "Add"}
            </Btn>
            <Btn variant="ghost" size="sm" onClick={() => setOpen(false)}>Close</Btn>
          </div>
          <p className="mnf-body italic" style={{ fontSize: 11.5, color: "var(--mnf-tg)", marginTop: 8, lineHeight: 1.5 }}>
            This adds one entry. Anything already in this block stays right where it is.
          </p>
        </div>
      )}
    </GlassCard>
  );
}
